import {
  Dialog,
  DialogBackdrop,
  DialogPanel,
  DialogTitle,
} from "@headlessui/react";
import { BoltIcon } from "@heroicons/react/24/outline";
import { CARD, HEADING, LINK_MUTED, PILL } from "@/onboarding/ui/theme";
import { orderedDemoTemplates } from "./templates";
import { useDemoGuideStore } from "./useDemoGuideStore";
import { useState } from "react";
import { PricingModal } from "@/ui-components/PricingModal";
import { getTrialCtaLabel } from "@/utils/currency";
import { trackTrialCtaClick } from "@/utils/gtm";

/**
 * The arrival screen: one question, five answers, and a real dashboard on the
 * other side of it.
 *
 * Built to the same spec as the onboarding WelcomeModal — max-w-4xl panel,
 * PILL header, nq-onb-rise entrance — because this is the first thing a
 * visitor sees and the previous version was a small flat card grid that looked
 * like a cookie banner next to it.
 *
 * It asks what they trade rather than what they want to see. The second is a
 * question about our feature list; the first is a question about them, and it
 * is the one that lets us hand back a screen built for their asset class
 * before the guide has said anything. That costs nothing to answer — templates
 * are deterministic and local, no API call, no credits, no wait.
 *
 * Heading matches the marketing CTA that sent them ("Tour the Platform"); if
 * that copy changes, change it in both places.
 */
export default function DemoWelcomeModal() {
  const [pricingOpen, setPricingOpen] = useState(false);
  const { pickAssetClass, dismissPicker } = useDemoGuideStore((s) => ({
    pickAssetClass: s.pickAssetClass,
    dismissPicker: s.dismissPicker,
  }));

  return (
    <>
    <PricingModal isOpen={pricingOpen} onClose={() => setPricingOpen(false)} />
    {/* Stood down while the price is up.
        This dialog is z-[100] and PricingModal is z-50, so opening the price
        from in here left the chooser sitting on top of it — the question
        still being asked over the answer they had just gone to look at.
        Hidden rather than dismissed: closing the price without buying is a
        change of mind, not a decision, and the chooser should be there when
        they come back to it. */}
    <Dialog
      open={!pricingOpen}
      onClose={dismissPicker}
      className="relative z-[100] no-drag"
    >
      <DialogBackdrop className="fixed inset-0 bg-black/70" />
      <div className="fixed inset-0 z-10 flex items-center justify-center sm:p-6">
        <DialogPanel className="relative flex h-full w-full flex-col overflow-hidden bg-gray-900 shadow-xl sm:h-auto sm:max-h-[88vh] sm:max-w-4xl sm:rounded-lg sm:border sm:border-gray-700">
          <div className="relative flex flex-1 flex-col overflow-y-auto px-6 py-7 sm:px-9 sm:py-8">
            <div className="flex items-start justify-between gap-6">
              <span className={PILL}>
                Demo
                <span className="text-gray-600">/</span>
                24h delayed
              </span>
              <div className="flex items-center gap-5">
                {/**
                 * The way past the question for someone who already knows.
                 *
                 * This screen asks what you trade before it will show you
                 * anything, which is right for a first visit and wrong for a
                 * returning one — someone who has seen the demo and come back
                 * to buy had to answer it, walk a run, and reach the end
                 * before a price appeared. A quiet link, not a second primary
                 * button: the five cards are still the question being asked.
                 *
                 * Its own analytics id, so arriving-ready reads separately
                 * from persuaded-by-the-run.
                 */}
                <button
                  type="button"
                  onClick={() => {
                    trackTrialCtaClick({ surface: "demo_guide" });
                    setPricingOpen(true);
                  }}
                  className="cursor-pointer border-none bg-transparent p-0 text-sm font-medium text-squawk-blue transition-colors hover:text-squawk-blue/80"
                  data-analytics-id="demo-guide-picker-trial-cta"
                >
                  {getTrialCtaLabel()}
                </button>
                <button
                  type="button"
                  onClick={dismissPicker}
                  className={LINK_MUTED}
                  data-analytics-id="demo-guide-picker-dismiss"
                >
                  Just let me look around
                </button>
              </div>
            </div>

            <div className="nq-onb-rise mt-7">
              <DialogTitle className={HEADING}>
                Tour the platform
              </DialogTitle>
              <p className="mt-2 max-w-2xl text-sm leading-relaxed text-gray-300">
                This is the real Newsquawk platform with real feeds running
                through it, delayed by exactly 24 hours. Nothing here is a
                mock-up and nothing is created on your behalf.
              </p>
            </div>

            <div className="nq-onb-rise mt-8">
              <p className="text-sm font-semibold text-gray-100">
                What do you trade?
              </p>
              <p className="mt-1 text-xs leading-relaxed text-gray-400">
                We'll lay out a dashboard for it — takes about a second.
              </p>

              <div className="mt-4 grid gap-2.5 sm:grid-cols-2 lg:grid-cols-3">
                {orderedDemoTemplates.map((template) => (
                  <button
                    key={template.id}
                    type="button"
                    onClick={() => pickAssetClass(template.assetClasses[0])}
                    className={`${CARD} group cursor-pointer p-4 text-left transition-colors hover:border-squawk-blue/60 focus:outline-none focus:ring-2 focus:ring-squawk-blue`}
                    // Keyed on the template id, which is also the feature_detail
                    // on asset_class_picked and the `flow` on the closing
                    // trial_cta_click — the two places the asset class is still
                    // recorded now that flow_step carries no variant field.
                    data-analytics-id={`demo-guide-asset-class-${template.id}`}
                  >
                    <span className="flex items-center gap-2 text-sm font-semibold text-gray-100">
                      {template.name}
                      <BoltIcon className="h-3.5 w-3.5 text-gray-600 transition-colors group-hover:text-squawk-blue" />
                    </span>
                    <span className="mt-1.5 block text-xs leading-relaxed text-gray-400">
                      {template.blurb}
                    </span>
                  </button>
                ))}
              </div>
            </div>

            <p className="mt-7 border-t border-gray-800 pt-4 text-2xs uppercase tracking-wide text-gray-500">
              No signup · nothing saved · change your mind whenever
            </p>
          </div>
        </DialogPanel>
      </div>
    </Dialog>
    </>
  );
}
